import React from "react";
import { Sun, Moon } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

export const ACCENT = "#38BDF8";

export const fmtDate = (iso, withTime = false) => {
  if (!iso) return "—";
  try { return format(parseISO(iso), withTime ? "dd/MM/yy HH:mm" : "dd/MM/yyyy", { locale: ptBR }); } catch { return "—"; }
};

export const fmtAgo = (iso) => {
  if (!iso) return "nunca";
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: ptBR }); } catch { return "—"; }
};

export const fmtMoney = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

export const PLAN_LABEL = { free: "Free", standard: "Standard", pro: "Pro" };
const PLAN_STYLE = {
  free: "bg-white/5 text-slate-300 ring-white/10",
  standard: "bg-sky-500/15 text-sky-300 ring-sky-400/30",
  pro: "bg-[#38BDF8]/20 text-[#7DD3FC] ring-[#38BDF8]/40",
};

export const PlanBadge = ({ plan, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${PLAN_STYLE[plan] || PLAN_STYLE.free}`}>{PLAN_LABEL[plan] || plan}</span>
);

export const OriginBadge = ({ origin }) => {
  const map = { stripe: ["Stripe", "text-emerald-300 bg-emerald-500/10 ring-emerald-400/30"], cortesia: ["Cortesia", "text-amber-300 bg-amber-500/10 ring-amber-400/30"], free: ["Free", "text-slate-400 bg-white/5 ring-white/10"] };
  const [label, cls] = map[origin] || map.free;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${cls}`}>{label}</span>;
};

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-2xl bg-[#111827]/80 ring-1 ring-white/[0.06] backdrop-blur-sm ${className}`}>
    {(title || action) && (
      <header className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/[0.06]">
        <h3 className="text-[13px] font-semibold tracking-wide text-slate-200 uppercase">{title}</h3>
        {action}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Kpi = ({ label, value, hint, accent = false, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-2xl bg-[#111827]/80 ring-1 ring-white/[0.06] p-5 transition-transform hover:-translate-y-0.5">
    {accent && <span className="absolute inset-x-0 top-0 h-[2px]" style={{ background: ACCENT }} />}
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-bold font-mono-jb text-white">{value}</div>
    {hint && <div className="mt-1 text-[12px] text-slate-400">{hint}</div>}
  </div>
);

export const Empty = ({ text }) => <div className="py-10 text-center text-[13px] text-slate-500">{text}</div>;

export const ThemeToggle = ({ isLight, onToggle, className = "" }) => (
  <button type="button" onClick={onToggle} data-testid="admin-theme-toggle" title={isLight ? "Modo escuro" : "Modo claro"} aria-label="Alternar tema"
    className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl text-[12px] text-slate-300 ring-1 ring-white/10 hover:bg-white/5 transition-colors ${className}`}>
    {isLight ? <Moon size={14} /> : <Sun size={14} />} {isLight ? "Escuro" : "Claro"}
  </button>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export const inputCls = "w-full h-10 rounded-xl bg-[#0B0F19] ring-1 ring-white/10 focus:ring-2 focus:ring-[#38BDF8] outline-none px-3 text-[13.5px] text-slate-100 placeholder:text-slate-500";
export const btnPrimary = "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold text-white bg-[#38BDF8] hover:bg-[#0EA5E9] transition-colors disabled:opacity-50";
export const btnGhost = "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-[13px] font-medium text-slate-300 ring-1 ring-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
